import React from 'react';

import { XMarkIcon } from '@heroicons/react/24/outline';

import { Text } from './Text';
import { Col, Row } from './Flex';
import { ActionIcon } from './ActionIcon';
import { DivProps, SpanProps } from './common-types';

import { cn } from 'domain/core';
import { useAlertState } from 'layout';
import { createId } from 'domain/utils';

type AlertVariant = 'success' | 'info' | 'warning' | 'error';

interface AlertProps extends Omit<DivProps, 'key'> {
	variant: AlertVariant;
	symbol?: symbol;
}
/**
 * Base alert component
 */
const Alert = ({
	className,
	variant,
	children,
	symbol,
	ref,
	...props
}: AlertProps) => {
	const removeAlert = useAlertState((state) => state.removeAlert);

	const borderClassName = React.useMemo(() => {
		switch (variant) {
			case 'error':
				return 'border-l-red-500';
			case 'info':
				return 'border-l-blue-500';
			case 'success':
				return 'border-l-green-500';
			case 'warning':
				return 'border-l-orange-500';
		}
	}, [variant]);

	const remove = React.useCallback(() => {
		if (!symbol) {
			return;
		}
		removeAlert(symbol);
	}, [removeAlert, symbol]);

	return (
		<Row
			className={cn(
				'relative',
				'max-w-[500px] rounded border border-l-4 bg-neutral-50 shadow-lg',
				'items-end gap-2 border-neutral-200 px-4 py-2',
				borderClassName,
				className
			)}
			{...props}>
			{children}
			{symbol && (
				<ActionIcon
					onClick={remove}
					className={cn('absolute top-1 right-1 p-[2px] ')}>
					<XMarkIcon className={cn('h-4 w-4')} />
				</ActionIcon>
			)}
		</Row>
	);
};

const AlertContent = React.forwardRef((props: DivProps, ref: any) => {
	return <Col {...props} ref={ref} className={cn('grow items-start gap-0')} />;
});

const AlertTitle = React.forwardRef(
	({ className, ...props }: SpanProps, ref: any) => {
		return <Text {...props} ref={ref} className={cn('text-sm font-bold')} />;
	}
);

const AlertMessage = React.forwardRef(
	({ className, ...props }: SpanProps, ref: any) => {
		return <Text {...props} ref={ref} className={cn('text-xs', className)} />;
	}
);

const AlertAction = React.forwardRef(
	({ className, ...props }: DivProps, ref: any) => {
		return (
			<Col
				{...props}
				ref={ref}
				className={cn('w-auto justify-end', className)}
			/>
		);
	}
);

type AlertFactory = ({
	message,
	title,
	symbol,
}: {
	message: string;
	title: string;
	symbol?: symbol;
}) => React.ReactElement;

const success: AlertFactory = ({ message, title, symbol }) => (
	<Alert variant='success' symbol={symbol ?? Symbol(createId())}>
		<Alert.Content>
			<Alert.Title>{title}</Alert.Title>
			<Alert.Message>{message}</Alert.Message>
		</Alert.Content>
	</Alert>
);
const error: AlertFactory = ({ message, title, symbol }) => (
	<Alert variant='error' symbol={symbol ?? Symbol(createId())}>
		<Alert.Content>
			<Alert.Title>{title}</Alert.Title>
			<Alert.Message>{message}</Alert.Message>
		</Alert.Content>
	</Alert>
);
const info: AlertFactory = ({ message, title, symbol }) => (
	<Alert variant='info' symbol={symbol ?? Symbol(createId())}>
		<Alert.Content>
			<Alert.Title>{title}</Alert.Title>
			<Alert.Message>{message}</Alert.Message>
		</Alert.Content>
	</Alert>
);
const warning: AlertFactory = ({ message, title, symbol }) => (
	<Alert variant='warning' symbol={symbol ?? Symbol(createId())}>
		<Alert.Content>
			<Alert.Title>{title}</Alert.Title>
			<Alert.Message>{message}</Alert.Message>
		</Alert.Content>
	</Alert>
);

AlertContent.displayName = 'AlertContent';
AlertTitle.displayName = 'AlertTitle';
AlertMessage.displayName = 'AlertMessage';
AlertAction.displayName = 'AlertAction';

/**
 * Shorcut for `<Alert variant='success' ...>`
 */
Alert.success = success;
/**
 * Shorcut for `<Alert variant='error' ...>`
 */
Alert.error = error;
/**
 * Shorcut for `<Alert variant='warning' ...>`
 */
Alert.warning = warning;
/**
 * Shorcut for `<Alert variant='info' ...>`
 */
Alert.info = info;

/**
 * Alert left content, usually title and message
 */
Alert.Content = AlertContent;
/**
 * Alert title
 */
Alert.Title = AlertTitle;
/**
 * Alert message
 */
Alert.Message = AlertMessage;
/**
 * Container to place a component. Usually an action like 'Undo'
 */
Alert.Action = AlertAction;

export { Alert };
